import { getSlotKey, parseSlotLabel, type SlotsState } from './slots';
import { enrich40ftSlotKeys } from './place-utils';
import type { ContainerType } from '../../types';

export type YardPlaceSceneContainer = {
  id: string;
  number: string;
  key?: string;
  tier: number;
  length?: number;
  type?: ContainerType;
  client?: {
    code: string;
  };
};

export type YardPlaceSceneBlock = {
  name: string;
  rowsLength: number;
  slotsLength: number;
  rotate?: number;
  position?: { x: number; y: number };
  roads?: ('top' | 'right' | 'bottom' | 'left')[];
};

export const createSlotsStateFromBlocks = (
  blocks: YardPlaceSceneBlock[],
  containersProp: YardPlaceSceneContainer[] = [],
): SlotsState => {
  const slotNames: Record<string, string> = {};
  const blockMaps: NonNullable<SlotsState['blockMaps']> = [];
  const blockPositions = new Map<string, { startCol: number; startRow: number }>();
  let nextColumn = 0;

  for (const [index, block] of blocks.entries()) {
    const position = block.position;
    const startColumn = position?.x ?? nextColumn;
    const startRow = position?.y ?? 0;
    const colsCount = block.slotsLength;
    const rowsCount = block.rowsLength;

    for (let rowIndex = 0; rowIndex < block.rowsLength; rowIndex++) {
      for (let slotIndex = 0; slotIndex < block.slotsLength; slotIndex++) {
        const col = startColumn + slotIndex;
        const row = startRow + rowIndex;
        const key = getSlotKey(col, row);

        slotNames[key] = `${block.name}(${rowIndex + 1},${slotIndex + 1})`;
      }
    }

    blockMaps.push({
      id: `${block.name}-${index}`,
      startCol: startColumn,
      startRow,
      colsCount,
      rowsCount,
      rotation: block.rotate ?? 0,
      roads: block.roads,
    });
    blockPositions.set(block.name.toUpperCase(), { startCol: startColumn, startRow });

    nextColumn = Math.max(nextColumn, startColumn + Math.max(colsCount, rowsCount) + 2);
  }

  const containers = containersProp.flatMap((container) => {
    const slotLabels = (container.key ?? '')
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean);
    const slotKeys = slotLabels.flatMap((slotLabel) => {
      const match = /^(.+)-(\d+)-(\d+)$/.exec(slotLabel);
      if (!match) return [];

      const blockPosition = blockPositions.get(match[1].toUpperCase());
      if (!blockPosition) return [];

      return [
        getSlotKey(
          blockPosition.startCol + Number(match[3]) - 1,
          blockPosition.startRow + Number(match[2]) - 1,
        ),
      ];
    });
    if (!slotKeys.length) return [];

    const normalizedSlotKeys = enrich40ftSlotKeys(slotKeys, slotNames, container.length);

    return [
      {
        id: container.id,
        number: container.number,
        tier: Number(container.tier) || 1,
        length: container.length,
        slotKey: normalizedSlotKeys[0],
        slotKeys: normalizedSlotKeys,
        ...(container.type ? { type: container.type } : {}),
        ...(container.client ? { client: container.client } : {}),
      },
    ];
  });

  return {
    containers,
    slotNames,
    blockMaps,
  };
};

export const getOrderedSlotKey = (slotName: string) => {
  const { col: row, row: slot } = parseSlotLabel(slotName);

  return getSlotKey(slot, row);
};
